<template>
  <cupertino-alert-dialog :title="$t('nolaNovelContestConfirmDialog.title')" :close="onClickOutSide">
    <template v-slot:content>
      <div class="px-1 py-1">
        {{ $t('nolaNovelContestConfirmDialog.message', { name: $props.name }) }}<br />
        {{ $t('nolaNovelContestConfirmDialog.instruction') }}
      </div>

      <!-- 投稿条件コンテナ -->
      <div class="submission-condition">
        <div class="condition-content">
          <div class="condition-label">{{ $t('nolaNovelContestConfirmDialog.submissionConditionLabel') }}</div>
          <div class="condition-value" v-html="submissionConditionText"></div>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <div class="flex flex-row flex-center">
        <div
          class="w-full border-right-1 flex flex-center pointer py-1 left-hover left-bottom-radius-10 font-bold"
          @click="onCancelClick"
        >
          <span>{{ $t('common.cancel') }}</span>
        </div>
        <div
          v-if="!published"
          class="w-full flex flex-center pointer py-1 right-hover right-bottom-radius-10 font-bold blue-text"
          @click="onSubmitClick"
        >
          <span class="color-red">{{ $t('nolaNovelContestConfirmDialog.postToNolaNovel') }}</span>
        </div>
        <div
          v-else-if="needsChangePublishStatus"
          class="w-full flex flex-center pointer py-1 right-hover right-bottom-radius-10 font-bold blue-text"
          @click="onSubmitClick"
        >
          <span class="color-red">{{ $t('nolaNovelContestConfirmDialog.changePublicSettings') }}</span>
        </div>
        <div
          v-else-if="!hasEpisodes"
          class="w-full flex flex-center pointer py-1 right-hover right-bottom-radius-10 font-bold blue-text"
          @click="onSubmitClick"
        >
          <span class="color-red">{{ $t('nolaNovelContestConfirmDialog.postEpisode') }}</span>
        </div>
      </div>
      <masked-loading v-if="isLoading" class="loading" />
    </template>
  </cupertino-alert-dialog>
</template>
<script lang="ts">
import Vue, { PropType } from "vue";
import CupertinoAlertDialog from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import MaskedLoading from "@/components/atoms/MaskedLoading.vue";

interface Data {
  isLoading: boolean;
}

interface Props {
  id: string;
  name: string;
  nolaNovelId?: string;
  isPublic?: boolean;
  isLimitedPublic?: boolean;
  isPrivate?: boolean;
  hasEpisodes?: boolean;
  published?: boolean;
  publishCondition?: string[] | null;
}

interface Methods {
  onClickOutSide: () => void;
  onCancelClick: () => void;
  onSubmitClick: () => void;
}

interface Computed {
  needsChangePublishStatus: boolean;
  submissionConditionText: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { CupertinoAlertDialog, MaskedLoading },
  data() {
    return {
      isLoading: false,
    };
  },
  props: {
    id: String as PropType<string>,
    name: String as PropType<string>,
    nolaNovelId: String as PropType<string>,
    isPublic: Boolean as PropType<boolean | undefined>,
    isLimitedPublic: Boolean as PropType<boolean | undefined>,
    isPrivate: Boolean as PropType<boolean | undefined>,
    hasEpisodes: Boolean as PropType<boolean | undefined>,
    published: Boolean as PropType<boolean | undefined>,
    publishCondition: Array as PropType<string[] | null>,
  },
  computed: {
    needsChangePublishStatus() {
      // publishCondition が空または未設定の場合
      const isPublishConditionEmpty = !this.publishCondition || this.publishCondition.length === 0;

      if (isPublishConditionEmpty) {
        // 条件: publishCondition が空または nullのときは[非公開でない]または[未投稿]の場合
        return !this.isPrivate || !this.published;
      }

      if (this.publishCondition.includes("public") && this.publishCondition.includes("limitedPublic")) {
        // 条件: publishCondition が ["public", "limitedPublic"]のときは[非公開]または[未投稿]の場合
        return this.isPrivate || !this.published;
      }

      if (this.publishCondition.includes("public")) {
        // 条件: publishCondition が ["public"]のときは[限定公開]または[非公開]または[未投稿]の場合
        return this.isLimitedPublic || this.isPrivate || !this.published;
      }

      if (this.publishCondition.includes("limitedPublic")) {
        // 条件: publishCondition が ["limitedPublic"]のときは[公開]または[非公開]または[未投稿]の場合
        return this.isPublic || this.isPrivate || !this.published;
      }

      // デフォルトで false を返す（全ての条件に該当しない場合）
      return false;
    },

    submissionConditionText(): string {
      if (this.publishCondition?.includes("public") && this.publishCondition?.includes("limitedPublic")) {
        // 順不同（publishConditionの配列順は考慮しないAppでは考慮している）
        return this.$t('nolaNovelContestConfirmDialog.conditions.publicAndLimitedPublic');
      }
      if (this.publishCondition?.includes("limitedPublic")) {
        return this.$t('nolaNovelContestConfirmDialog.conditions.limitedPublic');
      }
      if (this.publishCondition?.includes("public")) {
        return this.$t('nolaNovelContestConfirmDialog.conditions.public');
      }

      return this.$t('nolaNovelContestConfirmDialog.conditions.notSpecified');
    },
  },
  methods: {
    onClickOutSide() {
      this.$close(false);
    },
    onCancelClick() {
      this.$close(false);
    },
    async onSubmitClick() {
      this.isLoading = true;
      if (!this.published) {
        console.log("Nolaノベルで投稿する");
        this.$close("/novel/create");
        this.isLoading = false;
        return;
      }

      if (this.needsChangePublishStatus) {
        let isPublic = false;
        let isLimitedPublic = false;

        if (this.publishCondition?.includes("public") && this.publishCondition?.includes("limitedPublic")) {
          // 公開中・限定公開の両方が応募条件の場合 → 限定公開として設定
          isPublic = false;
          isLimitedPublic = true;
        } else if (this.publishCondition?.includes("public")) {
          // 公開のみが応募条件の場合
          isPublic = true;
          isLimitedPublic = false;
        } else if (this.publishCondition?.includes("limitedPublic")) {
          // 限定公開のみが応募条件の場合
          isPublic = false;
          isLimitedPublic = true;
        }

        await this.$store.dispatch("nolaNovelModule/updateNovel", {
          id: this.nolaNovelId,
          isPublic,
          isLimitedPublic,
        });

        this.$close("reopen");
        this.isLoading = false;
        return;
      }

      if (!this.hasEpisodes) {
        console.log("エピソードを投稿する");
        this.$close("/episode/create");
        this.isLoading = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.loading {
  z-index: 9001;
}

.submission-condition {
  margin: 0px 16px 16px 16px;
  padding: 12px;
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  text-align: center;
  color: var(--color-text-normal);
}

.condition-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.condition-label {
  color: var(--color-text-normal);
}

.condition-value {
  color: var(--color-text-normal);
}

:deep(.blue-text) {
  color: var(--color-primary);
}

.color-red {
  color: var(--color-warning);
}

.border-right-1 {
  border-right: solid 1px var(--color-border);
}
</style>
